import { Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, Output, Renderer2, SimpleChanges, ViewChild } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ValidationErrors } from "@angular/forms";
import { NbTagComponent, NbTagListComponent } from "@nebular/theme";
import { TagsInputOption } from "./tags-input-option.model";

@Component({
    selector: "kno2-tags-input",
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TagsInputComponent),
            multi: true
        }
    ],
    templateUrl: "./tags-input.component.html",
    styleUrls: ["./tags-input.component.scss"],
    standalone: false
})
export class TagsInputComponent implements OnChanges, ControlValueAccessor {
    @ViewChild("inputRef", { static: true }) public inputEl: ElementRef;
    @ViewChild("tagsList", { static: true }) public tagsList: NbTagListComponent;
    @Input() public containerId?: string;
    @Input() public disabled = false;
    @Input() public errors?: ValidationErrors;
    @Input() public options: Array<TagsInputOption> = [];
    @Input() public placeholder: string;
    @Output() public tagsChange: EventEmitter<Array<string>> = new EventEmitter<Array<string>>();
    public tags: Array<string> = [];
    public availableOptions: Array<TagsInputOption> = [{ value: "", selected: false }];
    public search: string;
    private onTouch: () => void;
    private onModelChange: (tags: Array<string>) => void;

    constructor(private renderer2: Renderer2) {}

    public ngOnChanges(changes: SimpleChanges): void {
        const { options } = changes;

        if (options && JSON.stringify(options?.previousValue) !== JSON.stringify(options.currentValue)) {
            this.tags = (options?.currentValue || [])?.filter((o: TagsInputOption) => o.selected).map((o: TagsInputOption) => o.value);

            this.setAvailableOptions();
        }
    }

    public writeValue(value: string | Array<string>): void {
        this.onTagAdd(value);
    }
    public registerOnChange(fn: () => void): void {
        this.onModelChange = fn;
    }
    public registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    public onBlur(): void {
        if (this.search) this.addSingleTag(this.search);
        if (this.onTouch) this.onTouch();
    }

    public clearTags(): void {
        this.tagsList.tags.forEach((x) => x._remove());

        this.emitAndUpdateControl();
    }

    public onSearchChange(): void {
        this.emitAndUpdateControl(true);
    }

    public onTagRemove(tagToRemove: NbTagComponent): void {
        this.updateOptionSelected(tagToRemove.text, false);
        this.tags.splice(this.tags.indexOf(tagToRemove.text), 1);

        this.emitAndUpdateControl();
    }

    public onTagAdd(value: string | Array<string>): void {
        if (!this.options || !value) return;

        if (value instanceof Array) value.forEach((tag) => this.addSingleTag(tag));
        else this.addSingleTag(value);
    }

    private updateOptionSelected(value: string, selected: boolean) {
        this.options.forEach((o) => {
            if (o.value === value) o.selected = selected;
        });
    }

    private addSingleTag(value: string): void {
        if (this.tags.includes(value)) return;

        const result = this.options.find((x) => x.value.toLowerCase().includes(value.toLowerCase()))?.value ?? value;

        this.updateOptionSelected(result, true);
        this.tags.push(result);

        this.search = "";

        this.emitAndUpdateControl();
    }

    private emitAndUpdateControl(skipEmission = false): void {
        if (this.onModelChange) this.onModelChange([...this.tags, this.search].filter((x) => !!x));

        if (!skipEmission) this.tagsChange.emit(this.tags);

        this.setAvailableOptions();

        if (!this.search) this.renderer2.setProperty(this.inputEl.nativeElement, "value", "");
    }

    private setAvailableOptions(): void {
        this.availableOptions = (this.options || []).filter((o) => !this.search || o.value.toLocaleLowerCase().includes(this.search.toLowerCase()));
    }
}
